"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, Loader2, MapPin } from "lucide-react";

import usePatientAvailability from "../../../hooks/usePatientAvailability";
import useRendezvous from "../../../hooks/useRendezvous";
import { getCurrentUser } from "../../../../authPatient/redux/thunks/authThunks";
import "./AppointmentScheduler.css";

/* ---------- Helpers ---------- */
const pad = (n) => String(n).padStart(2, "0");

const dateKey = (d) => {
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  return new Date(Date.UTC(y, m, day)).toISOString().slice(0, 10);
};

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const formatMonthDayOrdinal = (d) =>
  `${new Intl.DateTimeFormat("en-US", { month: "long" }).format(d)} ${ordinal(d.getDate())}`;

const getErrorText = (err, fallback) => {
  if (!err) return fallback || "An error occurred";
  const data = err.data || err.response?.data || err;
  return (
    (typeof data?.message === "string" && data.message) ||
    (Array.isArray(data?.message) && data.message[0]) ||
    data?.error || data?.detail || err.message || fallback || "An error occurred"
  );
};

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const WEEK_DAYS_14 = [
  "SUN","MON","TUE","WED","THU","FRI","SAT",
  "SUN","MON","TUE","WED","THU","FRI","SAT",
];

/* ---------- URL utils ---------- */
const buildEmbedMapUrl = (doctor) => {
  const q =
    doctor?.address || doctor?.clinicAddress || doctor?.practicePlace ||
    [doctor?.city, doctor?.country].filter(Boolean).join(", ") || "clinic";
  return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;
};

const buildDirectionLink = (doctor) => {
  const q =
    doctor?.address || doctor?.clinicAddress || doctor?.practicePlace ||
    [doctor?.city, doctor?.country].filter(Boolean).join(", ") || "clinic";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

export default function AppointmentScheduler({ doctor, onBookAppointment }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [appointmentType, setAppointmentType] = useState("in-person");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [hoveredTime, setHoveredTime] = useState(null);
  const [weekStartDate, setWeekStartDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear] = useState(new Date().getFullYear());
  const [timeFilter, setTimeFilter] = useState("all");
  const [toast, setToast] = useState({ open: false, text: "", type: "success" });
  const [isBooking, setIsBooking] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState("");

  const { slotsByKey, loadingSlots, slotsError, loadSlots } = usePatientAvailability();
  const { bookRendezvous, creating: creatingRDV, createError: rdvError, lastCreated } = useRendezvous();

  const selectRef = useRef(null);
  const lastLoadedKeyRef = useRef(null);
  const lastBookedRef = useRef(null);

  const rawMedecinId =
    doctor?.id ?? doctor?.medecinId ?? doctor?.userId ?? doctor?.doctorId ??
    doctor?.doctor?.id ?? doctor?.doctor?.userId;
  const medecinId = rawMedecinId != null ? String(rawMedecinId) : null;
  const hasValidMedecinId = Boolean(medecinId && medecinId.trim().length > 0);

  /* ---- Effects ---- */
  useEffect(() => { dispatch(getCurrentUser()); }, [dispatch]);

  useEffect(() => {
    if (selectedDate && hasValidMedecinId && !loadingSlots) {
      const key = `${medecinId}|${dateKey(selectedDate)}`;
      if (key !== lastLoadedKeyRef.current) {
        lastLoadedKeyRef.current = key;
        loadSlots(medecinId, dateKey(selectedDate));
      }
    }
  }, [selectedDate, hasValidMedecinId, medecinId, loadSlots, loadingSlots]);

  useEffect(() => {
    if (lastCreated && lastCreated !== lastBookedRef.current) {
      lastBookedRef.current = lastCreated;
      const modeKey = appointmentType === "in-person"
        ? "appointmentScheduler.success.modeInPerson"
        : "appointmentScheduler.success.modeVideo";
      const msg = t("appointmentScheduler.success.scheduled", {
        mode: t(modeKey),
        date: selectedDate ? formatMonthDayOrdinal(selectedDate) : "",
        time: selectedTime || "",
      });
      setToast({ open: true, text: msg, type: "success" });
      setTimeout(() => setToast({ open: false, text: "", type: "success" }), 4000);
      setSelectedDate(null);
      setSelectedTime(null);
      setIsBooking(false);
      onBookAppointment?.({
        date: selectedDate, time: selectedTime, type: appointmentType,
        doctor: doctor?.name || t("appointmentScheduler.defaults.doctor"),
        appointment: lastCreated,
      });
    }
  }, [lastCreated, appointmentType, selectedDate, selectedTime, doctor, onBookAppointment, t]);

  useEffect(() => {
    if (rdvError && isBooking) {
      setToast({ open: true, text: getErrorText(rdvError, t("appointmentScheduler.errors.generic")), type: "error" });
      setTimeout(() => setToast({ open: false, text: "", type: "success" }), 4000);
      setIsBooking(false);
    }
  }, [rdvError, isBooking, t]);

  /* ---- Helpers ---- */
  const getAvailableTimesForDate = (date) => {
    if (!date || !hasValidMedecinId) return [];
    const key = `${medecinId}|${dateKey(date)}`;
    const slots = slotsByKey?.[key] ?? [];
    const now = new Date();
    const isSameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    const labelsSet = new Set();
    const labels = [];
    for (const s of slots) {
      if (!s?.isAvailable || !s?.startTime) continue;
      const dt = new Date(s.startTime);
      if (isSameDay && dt <= now) continue;
      const h = dt.getHours(), m = dt.getMinutes();
      const period = h >= 12 ? "PM" : "AM";
      const label = `${h % 12 || 12}:${pad(m)} ${period}`;
      if (!labelsSet.has(label)) { labelsSet.add(label); labels.push(label); }
    }
    return labels;
  };

  /* Génère 14 jours depuis une référence fixe 'base' */
  const generateCalendarDays = () => {
    const base = new Date(weekStartDate);
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() - base.getDay()); // rewind au dimanche
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = [];
    for (let i = 0; i < 14; i++) {
      const date = new Date(base);
      date.setDate(base.getDate() + i); // toujours depuis 'base'
      days.push({ day: date.getDate(), date, disabled: false, isToday: date.toDateString() === today.toDateString() });
    }
    return days;
  };

  /* ---- Handlers ---- */
  const handleDateClick = (day) => {
    if (!day.disabled) { setSelectedDate(day.date); setSelectedTime(null); }
  };

  const handleTimeClick = (time) => {
    if (selectedDate) setSelectedTime(String(time).trim());
  };

  const handlePrevWeek = () => {
    const d = new Date(weekStartDate);
    d.setDate(d.getDate() - 7);
    setWeekStartDate(d);
    setSelectedMonth(d.getMonth());
  };

  const handleNextWeek = () => {
    const d = new Date(weekStartDate);
    d.setDate(d.getDate() + 7);
    setWeekStartDate(d);
    setSelectedMonth(d.getMonth());
  };

  const handleMonthChange = (e) => {
    const idx = parseInt(e.target.value);
    setSelectedMonth(idx);
    const d = new Date(selectedYear, idx, 1);
    setWeekStartDate(d);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  /* Ouvre le popup "reason" avant de confirmer */
  const handleBookAppointment = () => {
    if (!selectedDate || !selectedTime || !hasValidMedecinId || isBooking) return;
    setShowReasonModal(true);
  };

  /* skipReason = true → on n'envoie pas le texte saisi */
  const handleConfirmBooking = async (skipReason = false) => {
    setShowReasonModal(false);
    setIsBooking(true);

    const key = `${medecinId}|${dateKey(selectedDate)}`;
    const slots = slotsByKey?.[key] ?? [];

    const matchingSlot = slots.find((slot) => {
      if (!slot?.startTime) return false;
      const dt = new Date(slot.startTime);
      const h = dt.getHours(), m = dt.getMinutes();
      const period = h >= 12 ? "PM" : "AM";
      return `${h % 12 || 12}:${pad(m)} ${period}` === selectedTime;
    });

    if (!matchingSlot) {
      setToast({ open: true, text: t("appointmentScheduler.errors.slotNotAvailable"), type: "error" });
      setTimeout(() => setToast({ open: false, text: "", type: "success" }), 3000);
      setIsBooking(false);
      return;
    }
    if (new Date(matchingSlot.startTime) <= new Date()) {
      setToast({ open: true, text: t("appointmentScheduler.errors.slotPassed"), type: "error" });
      setTimeout(() => setToast({ open: false, text: "", type: "success" }), 3000);
      setIsBooking(false);
      return;
    }

    try {
      await bookRendezvous({
        medecinId,
        startTime: matchingSlot.startTime,
        endTime: matchingSlot.endTime,
        type: "standard",
        reason: skipReason ? null : reason.trim() || null,
      });
      await loadSlots(medecinId, dateKey(selectedDate));
      setReason("");
    } catch (e) {
      setToast({ open: true, text: getErrorText(e, t("appointmentScheduler.errors.generic")), type: "error" });
      setTimeout(() => setToast({ open: false, text: "", type: "success" }), 3000);
      setIsBooking(false);
    }
  };

  /* ---- Derived ---- */
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMonthName = new Intl.DateTimeFormat(i18n.language || "en", { month: "long" }).format(today);
  const isDateSelected = !!selectedDate;
  const availableTimes = isDateSelected ? getAvailableTimesForDate(selectedDate) : [];
  const filteredTimes = availableTimes.filter((t) =>
    timeFilter === "all" ? true : timeFilter === "am" ? t.includes("AM") : t.includes("PM")
  );
  const mapUrl = useMemo(() => buildEmbedMapUrl(doctor), [doctor]);
  const directionHref = useMemo(() => buildDirectionLink(doctor), [doctor]);
  const addressText =
    doctor?.address || doctor?.clinicAddress || doctor?.practicePlace ||
    [doctor?.city, doctor?.country].filter(Boolean).join(", ") ||
    t("appointmentScheduler.defaults.addressNA");
  const clinicName =
    doctor?.clinic || doctor?.practicePlace || doctor?.name || t("appointmentScheduler.defaults.clinic");
  const disabled = !isDateSelected || !selectedTime || isBooking || creatingRDV || !hasValidMedecinId;
  const calendarDays = generateCalendarDays();

  /* ---- Render ---- */
  return (
    <div className="appointment-scheduler compact">
      <div className="scheduler-grid">

        {/* ===== LEFT PANEL ===== */}
        <div className="left-panell">
          <div className="header-row">
            <h3 className="panel-title">{t("appointmentScheduler.header.title")}</h3>
            <div className="as-header">
              <div className="radio-tabs" role="tablist" aria-label={t("appointmentScheduler.header.modeAria")}>
                <label className={`radio-label ${appointmentType === "in-person" ? "is-active" : ""}`} role="tab" aria-selected={appointmentType === "in-person"}>
                  <input type="radio" name="appointmentType" checked={appointmentType === "in-person"} onChange={() => setAppointmentType("in-person")} className="hidden-radio" />
                  <span className="radio-text">{t("appointmentScheduler.modes.inPerson")}</span>
                </label>
                <label className="radio-label is-disabled" role="tab" aria-selected="false" aria-disabled="true">
                  <input type="radio" name="appointmentType" disabled className="hidden-radio" />
                  <span className="radio-text">{t("appointmentScheduler.modes.videoCall")}</span>
                </label>
              </div>
            </div>
          </div>

          <div className="alert-box">
            <div className="alert-content">
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="35" viewBox="0 0 35 35" fill="none">
                <path d="M21.8757 2.91699H8.75065C7.9771 2.91699 7.23524 3.22428 6.68826 3.77126C6.14128 4.31824 5.83398 5.06011 5.83398 5.83366V29.167C5.83398 29.9405 6.14128 30.6824 6.68826 31.2294C7.23524 31.7764 7.9771 32.0837 8.75065 32.0837H26.2506C27.0242 32.0837 27.7661 31.7764 28.313 31.2294C28.86 30.6824 29.1673 29.9405 29.1673 29.167V10.2087L21.8757 2.91699Z" stroke="#D10000" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17.5 13.125V18.9583" stroke="#D10000" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17.5 24.792H17.5146" stroke="#D10000" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div>
                <p className="alert-title">{t("appointmentScheduler.videoUnavailable.title")}</p>
                <p className="alert-text">{t("appointmentScheduler.videoUnavailable.text")}</p>
              </div>
            </div>
          </div>

          {(slotsError || rdvError) && (
            <div className="alert-box" style={{ marginTop: "1rem", borderColor: "#ef4444" }}>
              <div className="alert-content">
                <AlertTriangle style={{ color: "#ef4444", width: 20, height: 20, flexShrink: 0 }} />
                <div>
                  <p className="alert-title">{t("appointmentScheduler.errors.title")}</p>
                  <p className="alert-text">{getErrorText(slotsError, "") || getErrorText(rdvError, t("appointmentScheduler.errors.generic"))}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== CENTER PANEL ===== */}
        <div className="center-panel">

          {/* CALENDAR */}
          <div className="calendar-container">
            <div className="calendar-header">
              <div className="calendar-header-left">
                <span className="today-text">
                  {t("appointmentScheduler.calendar.todayLine", {
                    day: today.getDate(),
                    month: String(todayMonthName || "").toLowerCase(),
                    year: today.getFullYear(),
                  })}
                </span>
                <div className="calendar-nav-buttons">
                  <button onClick={handlePrevWeek} className="nav-button nav-button-small" aria-label={t("appointmentScheduler.calendar.prev")}>
                    <ChevronLeft className="nav-icon" />
                  </button>
                  <button onClick={handleNextWeek} className="nav-button nav-button-small" aria-label={t("appointmentScheduler.calendar.next")}>
                    <ChevronRight className="nav-icon" />
                  </button>
                </div>
              </div>

              <div className="calendar-header-right">
                <div className="month-wrapper">
                  <select ref={selectRef} value={selectedMonth} onChange={handleMonthChange} className="month-minimal" aria-label={t("appointmentScheduler.calendar.month")}>
                    {MONTHS.map((month, index) => (
                      <option key={month} value={index}>{t(`appointmentScheduler.months.${month}`)}</option>
                    ))}
                  </select>
                  <svg
                    onClick={() => { if (selectRef.current) { selectRef.current.focus(); selectRef.current.dispatchEvent(new MouseEvent("mousedown", { bubbles: true })); } }}
                    className="month-arrow" xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none" style={{ cursor: "pointer" }} aria-hidden
                  >
                    <path d="M1 1L6 6L11 1" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* wrapper scrollable: 1 seule ligne header + 1 seule ligne jours */}
            <div className="calendar-two-weeks-wrapper">
              <div className="calendar-week-header">
                {WEEK_DAYS_14.map((d, i) => (
                  <div key={`wh-${i}`} className="calendar-week-day">
                    {t(`appointmentScheduler.week.${d}`)}
                  </div>
                ))}
              </div>

              <div className="calendar-grid-two-weeks">
                {calendarDays.map((day, idx) => {
                  const isSelected = selectedDate && day.date && selectedDate.toDateString() === day.date.toDateString();
                  return (
                    <button
                      key={idx}
                      className={`calendar-day-small ${day.disabled ? "disabled" : ""} ${isSelected ? "active-day" : day.isToday ? "today-day" : ""}`}
                      onClick={() => handleDateClick(day)}
                      disabled={day.disabled}
                      aria-label={t("appointmentScheduler.calendar.dayAria", { day: day.day })}
                    >
                      {day.day}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* TIME SLOTS */}
          <div className="time-slots-section">
            <div className="time-slots-header">
              <h4 className="time-slots-title">{t("appointmentScheduler.times.title")}</h4>
              <div className="time-slots-filters">
                <button className={`time-filter-btn ${timeFilter === "am" ? "active" : ""}`} onClick={() => setTimeFilter(timeFilter === "am" ? "all" : "am")} title={t("appointmentScheduler.times.morning")} aria-label={t("appointmentScheduler.times.morning")}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 16c2.209 0 4-1.791 4-4s-1.791-4-4-4-4 1.791-4 4 1.791 4 4 4Z" stroke="#546881" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 2v2M12 20v2M4.93 4.93 6.34 6.34M17.66 17.66 19.07 19.07M2 12h2M20 12h2M6.34 17.66 4.93 19.07M19.07 4.93 17.66 6.34" stroke="#546881" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button className={`time-filter-btn ${timeFilter === "pm" ? "active" : ""}`} onClick={() => setTimeFilter(timeFilter === "pm" ? "all" : "pm")} title={t("appointmentScheduler.times.afternoon")} aria-label={t("appointmentScheduler.times.afternoon")}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M18.586 10.072c-.093 1.736-.688 3.408-1.711 4.814-1.024 1.406-2.433 2.485-4.056 3.107-1.624.621-3.393.76-5.094.398-1.7-.362-3.26-1.209-4.49-2.439-1.23-1.229-2.077-2.789-2.439-4.49-.362-1.7-.224-3.47.398-5.093C1.816 4.745 2.895 3.337 4.3 2.313 5.706 1.289 7.377.694 9.114.6c.405-.022.617.46.402.803-.719 1.15-1.027 2.51-.874 3.858.154 1.348.76 2.604 1.72 3.563.959.96 2.216 1.566 3.564 1.719 1.349.154 2.709-.153 3.86-.873.344-.215.825-.004.803.4Z" stroke="#A3ADBB" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="time-slots-container">
              {!isDateSelected && <div className="no-slots">{t("appointmentScheduler.times.selectDateFirst")}</div>}
              {isDateSelected && loadingSlots && (
                <div className="no-slots">
                  <Loader2 size={16} style={{ marginRight: 6 }} className="animate-spin" />
                  {t("appointmentScheduler.times.loading", "Loading...")}
                </div>
              )}
              {isDateSelected && !loadingSlots && filteredTimes.length === 0 && (
                <div className="no-slots">{t("appointmentScheduler.times.noneForPeriod")}</div>
              )}
              {isDateSelected && !loadingSlots && filteredTimes.map((time, idx) => {
                const label = String(time).trim();
                const isSel = selectedTime === label;
                const showConfirm = !isSel && hoveredTime === label;
                return (
                  <button
                    key={idx}
                    className={`time-slot ${isSel ? "selected" : ""} ${showConfirm ? "confirm-hover" : ""}`}
                    onClick={() => handleTimeClick(label)}
                    onMouseEnter={() => setHoveredTime(label)}
                    onMouseLeave={() => setHoveredTime(null)}
                    aria-label={t("appointmentScheduler.times.slotAria", { time: label })}
                  >
                    {showConfirm ? t("appointmentScheduler.times.confirm") : label}
                  </button>
                );
              })}
            </div>

            {!hasValidMedecinId && (
              <div className="alert-box" style={{ marginTop: "1rem", borderColor: "#ef4444" }}>
                <div className="alert-content">
                  <AlertTriangle style={{ color: "#ef4444", width: 20, height: 20, flexShrink: 0 }} />
                  <div>
                    <p className="alert-title">{t("appointmentScheduler.errors.missingDoctorIdTitle")}</p>
                    <p className="alert-text">{t("appointmentScheduler.errors.missingDoctorIdText")}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* LOCATION */}
          <div className="location-panel">
            <div className="location-header">
              <MapPin size={16} />
              <span className="loc-title">{t("appointmentScheduler.location.title")}</span>
            </div>
            <div className="location-grid">
              <div className="location-info">
                <div className="clinic-name">{clinicName}</div>
                <div className="clinic-address"><span>{addressText}</span></div>
                <a className="loc-directions-btn" href={directionHref} target="_blank" rel="noopener noreferrer">
                  {t("appointmentScheduler.location.directions")}
                  {" "}
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M9.69119 14.4577C9.71652 14.5208 9.76055 14.5747 9.81737 14.6121C9.87419 14.6494 9.94109 14.6685 10.0091 14.6668C10.0771 14.665 10.1429 14.6426 10.1977 14.6023C10.2526 14.5621 10.2938 14.506 10.3159 14.4417L14.6492 1.77503C14.6705 1.71596 14.6746 1.65203 14.6609 1.59073C14.6473 1.52943 14.6164 1.47329 14.572 1.42888C14.5276 1.38447 14.4715 1.35363 14.4102 1.33996C14.3489 1.32629 14.2849 1.33036 14.2259 1.3517L1.55919 5.68503C1.49485 5.70709 1.4388 5.74831 1.39857 5.80314C1.35833 5.85798 1.33584 5.92381 1.33409 5.9918C1.33235 6.05979 1.35145 6.12669 1.38883 6.18351C1.4262 6.24034 1.48007 6.28437 1.54319 6.3097L6.82985 8.4297C6.99698 8.49661 7.14882 8.59667 7.27623 8.72385C7.40364 8.85103 7.50398 9.00269 7.57119 9.1697L9.69119 14.4577Z" stroke="#1e1e1e" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14.5687 1.43164L7.27539 8.72431" stroke="#1e1e1e" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
              <div className="location-map">
                <iframe src={mapUrl} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title={t("appointmentScheduler.location.mapTitle")} />
              </div>
            </div>
          </div>
        </div>

        {/* ===== FOOTER ===== */}
        <div className="acknowledge-section">
          <button className={`book-button ${disabled ? "disabled" : ""}`} disabled={disabled} onClick={handleBookAppointment}>
            {isBooking || creatingRDV ? (
              <span style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "center" }}>
                <Loader2 className="animate-spin" size={14} />
                <span>{t("appointmentScheduler.actions.booking")}</span>
              </span>
            ) : t("appointmentScheduler.actions.sendRequest")}
          </button>
        </div>
      </div>

      {/* TOAST */}
      <div className={`toast ${toast.open ? "show" : ""} ${toast.type === "error" ? "toast-error" : ""}`} role="status" aria-live="polite">
        {toast.type === "success" ? <CheckCircle className="toast-icon" /> : <AlertTriangle className="toast-icon" style={{ color: "#ef4444" }} />}
        <span className="toast-text">{toast.text}</span>
      </div>

      {/* ── Reason Modal ── */}
      {showReasonModal && (
        <div className="reason-modal-overlay" onClick={() => setShowReasonModal(false)}>
          <div className="reason-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="reason-modal__title">Reason for consultation</h3>
            <p className="reason-modal__subtitle">Optional — help your doctor prepare for your visit</p>
            <textarea
              className="reason-modal__textarea"
              placeholder="Describe your symptoms or reason for visit..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <div className="reason-modal__char">{reason.length}/500</div>
            <div className="reason-modal__actions">
              <button
                className="reason-modal__skip"
                onClick={() => handleConfirmBooking(true)}
              >
                Skip & Send
              </button>
              <button
                className="reason-modal__confirm"
                onClick={() => handleConfirmBooking(false)}
                disabled={isBooking}
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}